import { IconCheck } from '@developerz/ui';
import { type JSX, Show } from 'solid-js';
import type { SelectOption } from './Select';

// One row of `Select.tsx`'s listbox (epic #5620 slice 02) — the `ModelPickerRow`
// pattern (an `aria-activedescendant` option; focus never lands here, so no
// tabindex and no key handler of its own) over the generic `SelectOption` shape
// instead of a `PickerRow`.

export type SelectOptionRowProps<V extends string> = {
  readonly option: SelectOption<V>;
  readonly domId: string;
  /** The listbox cursor (keyboard) is on this row. Never true for a disabled
   *  row — the cursor skips disabled options entirely (the caller's `active`
   *  memo is computed over the selectable subset only). */
  readonly active: boolean;
  /** This row is the control's current value. */
  readonly current: boolean;
  /** Absent on a disabled option — the row still RENDERS (so a caller can see
   *  why a choice is unavailable), it just cannot be picked, the same
   *  "absent = unpickable" contract `ModelPickerRow.onPick?` uses. */
  readonly onPick?: (() => void) | undefined;
};

export function SelectOptionRow<V extends string>(props: SelectOptionRowProps<V>): JSX.Element {
  return (
    // biome-ignore lint/a11y/useFocusableInteractive: an aria-activedescendant option; focus stays on the listbox/combobox by design, same as ModelPickerRow.
    // biome-ignore lint/a11y/useKeyWithClickEvents: the keyboard path is the listbox's own Enter/Space, not a per-option listener.
    <div
      role="option"
      id={props.domId}
      class="select__option"
      classList={{ 'select__option--active': props.active }}
      aria-selected={props.current}
      aria-disabled={props.onPick === undefined}
      // Keep focus on the listbox/filter input: a mousedown would blur it before the click lands.
      onMouseDown={(e) => e.preventDefault()}
      onClick={() => props.onPick?.()}
    >
      <span class="select__option-label">{props.option.label}</span>
      <Show when={props.option.hint}>
        <span class="select__option-hint">{props.option.hint}</span>
      </Show>
      <Show when={props.current}>
        <IconCheck class="select__check" size="1rem" />
      </Show>
    </div>
  );
}
